import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import ShieldGPSAxiosClient from '../../lib/ShieldGPSAxiosClient';
import { Device } from '../../models/Device';

export function RenameDeviceDialog({ device, handleClose }: {
  device: Device;
  handleClose: () => void;
}) {
  const [name, setName] = useState(device.name);
  const queryClient = useQueryClient();
  const rename = useMutation<{ name: string }, AxiosError<{ error: string }>, string>(
    async (newName) => {
      const response = await ShieldGPSAxiosClient.patch<{ name: string }>(
        `/admin/user-search/devices/${device.imei}/name`, { name: newName },
      );
      return response.data;
    },
    {
      onSuccess: ({ name: savedName }) => {
        queryClient.setQueryData<Device | null | undefined>(['device-lookup', device.imei], (current) =>
          current ? { ...current, name: savedName } : current);
        queryClient.invalidateQueries(['device-lookup', device.imei]);
        if (device.owner) {
          queryClient.invalidateQueries(['user-device-lookup', device.owner.id.toString()]);
        }
        handleClose();
      },
    },
  );
  const canSave = name.trim().length > 0 && name.trim() !== device.name && !rename.isLoading;

  return (
    <Dialog open onClose={rename.isLoading ? undefined : handleClose} fullWidth maxWidth="xs"
      aria-labelledby="rename-device-title" aria-describedby="rename-device-description">
      <form onSubmit={(event) => {
        event.preventDefault();
        if (canSave) rename.mutate(name.trim());
      }}>
        <DialogTitle id="rename-device-title">Rename device</DialogTitle>
        <DialogContent>
          <DialogContentText id="rename-device-description">
            This changes the name shown in the customer's ShieldGPS app.
          </DialogContentText>
          <TextField
            autoFocus fullWidth margin="normal" label="Device name"
            value={name} disabled={rename.isLoading}
            onChange={(event) => setName(event.target.value)} />
          {rename.isError && (
            <Alert severity="error">
              {rename.error.response?.data?.error || 'Could not rename the device. Please try again.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={rename.isLoading}>Cancel</Button>
          <LoadingButton type="submit" variant="contained" loading={rename.isLoading} disabled={!canSave}>
            Save name
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
